import { Button, Form, Table, Tag, message, Modal } from "antd";
import { EditOutlined, CloseCircleFilled } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import CustomCheckBox from "../../../../components/checkbox/CustomCheckBox";
import { serverUrl } from "../../../../nestserver";
import DayPermissionTag from "../../../../components/dayPermission/DayPermissionTag";

const AttendanceTable = ({
  attendanceData,
  classId,
  selectedDate,
  isAdmin,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [attendanceState, setAttendanceState] = useState({});
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (attendanceData?.data?.result && !isSaving) {
      const records = Array.isArray(attendanceData.data.result)
        ? attendanceData.data.result
        : attendanceData.data.result.data || [];
      const classDetails = attendanceData?.data?.result?.classDetails;

      const isClassCheckedIn = !!(
        classDetails?.check_in_time || records.some((r) => r.is_checked_in)
      );
      const isClassCheckedOut = !!(
        classDetails?.check_out_time || records.some((r) => r.is_checked_out)
      );

      const initialState = {};
      records.forEach((record) => {
        const studentId = record?.student?._id;
        if (!studentId) return;

        const permission = record?.permission;

        initialState[studentId] = {
          _id: record._id || null,
          // An approved leave / Day Permission excuses a session; it must not
          // default to "present", or the bulk submit would mark them in.
          check_in: isClassCheckedIn
            ? !!record.is_checked_in
            : !permission?.hasPermission && !permission?.morningPermission,
          check_out: isClassCheckedOut
            ? !!record.is_checked_out
            : !permission?.hasPermission && !permission?.afternoonPermission,
          is_checked_in: !!record.is_checked_in,
          is_checked_out: !!record.is_checked_out,
          permission: permission,
          student_name: `${record?.student?.first_name} ${record?.student?.last_name}`,
        };
      });

      setAttendanceState(initialState);
    }
  }, [attendanceData, isSaving]);

  const handleCheckboxChange = (studentId, field, value) => {
    setAttendanceState((prev) => {
      const newState = { ...prev };
      const studentState = { ...newState[studentId] };

      if (field === "check_in") {
        studentState.check_in = value;
      } else if (field === "check_out") {
        studentState.check_out = value;
      }

      newState[studentId] = studentState;
      return newState;
    });
  };

  const records = attendanceData?.data?.result
    ? Array.isArray(attendanceData.data.result)
      ? attendanceData.data.result
      : attendanceData.data.result.data || []
    : [];

  const classDetails = attendanceData?.data?.result?.classDetails;
  const isHoliday = !!attendanceData?.data?.result?.holiday?.isHoliday;

  const isCheckedIn = classDetails?.check_in_time
    ? true
    : records.length > 0 && records.some((r) => r.is_checked_in);
  const isCheckedOut = classDetails?.check_out_time
    ? true
    : records.some((r) => r.is_checked_out);

  const handleCheckIn = async () => {
    setIsCheckingIn(true);
    try {
      const payloadData = Object.keys(attendanceState).map((studentId) => {
        const state = attendanceState[studentId];
        return {
          student_id: studentId,
          is_checked_in: !!state.check_in,
        };
      });

      const payload = {
        date: selectedDate,
        student_attendance: payloadData,
      };

      await axios.post(
        `${serverUrl}/montessori/attendance/check-in/class/${classId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await queryClient.invalidateQueries([
        "montessori-attendance",
        classId,
        selectedDate,
      ]);

      message.success("Morning Attendance marked successfully");
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Failed to mark morning attendance",
      );
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    setIsCheckingOut(true);
    try {
      const payloadData = Object.keys(attendanceState).map((studentId) => {
        const state = attendanceState[studentId];
        return {
          student_id: studentId,
          is_checked_out: state.check_out,
        };
      });

      const payload = {
        date: selectedDate,
        student_attendance: payloadData,
      };

      await axios.post(
        `${serverUrl}/montessori/attendance/check-out/class/${classId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await queryClient.invalidateQueries([
        "montessori-attendance",
        classId,
        selectedDate,
      ]);

      message.success("Afternoon Attendance marked successfully");
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.error?.errors ||
          "Failed to mark afternoon attendance",
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Edit Modal State
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);

  const handleEditClick = (record) => {
    const studentId = record?.student?._id;
    setEditingStudent({
      studentId,
      _id: attendanceState[studentId]?._id,
      studentName: `${record?.student?.first_name} ${record?.student?.last_name}`,
      check_in: attendanceState[studentId]?.is_checked_in ?? false,
      check_out: attendanceState[studentId]?.is_checked_out ?? false,
    });
    setIsEditModalVisible(true);
  };

  const handleEditSave = async () => {
    if (!editingStudent) return;
    setIsSaving(true);
    try {
      const payload = {
        date: selectedDate,
        student_attendance: [
          {
            _id: editingStudent._id,
            student_id: editingStudent.studentId,
            is_checked_in: editingStudent.check_in,
            is_checked_out: editingStudent.check_out,
          },
        ],
      };

      await axios.patch(
        `${serverUrl}/montessori/attendance/class/${classId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );

      await queryClient.invalidateQueries([
        "montessori-attendance",
        classId,
        selectedDate,
      ]);

      message.success("Attendance updated successfully");
      setIsEditModalVisible(false);
      setEditingStudent(null);
    } catch (err) {
      console.error(err);
      message.error("Failed to update attendance");
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      align: "center",
      render: (record) => (
        <div className="flex items-center justify-center gap-2">
          <div
            style={{ color: "#111111" }}
            className=" text-base font-semibold text-center capitalize"
          >
            {`${record?.student?.first_name} ${record?.student?.last_name}`}
          </div>
          {(isCheckedIn || isCheckedOut) && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(record);
              }}
              className="cursor-pointer p-1"
              title="Edit Attendance"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditOutlined style={{ color: "#1890ff", fontSize: "1.2rem" }} />
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Morning",
      key: "morning",
      align: "center",
      render: (record) => {
        const studentId = record?.student?._id;
        const studentState = attendanceState[studentId];
        const permission = studentState?.permission;
        const hasPermission = permission?.hasPermission;
        const alreadyCheckedIn = studentState?.is_checked_in;

        return (
          <div className="flex items-center justify-center gap-2">
            {isHoliday ? (
              <span className="text-gray-400" title="Holiday">-</span>
            ) : hasPermission ? (
              <CloseCircleFilled
                style={{ color: "#f5222d", fontSize: "20px" }}
              />
            ) : (
              <CustomCheckBox
                checked={studentState?.check_in}
                onChange={(e) =>
                  handleCheckboxChange(studentId, "check_in", e.target.checked)
                }
                disabled={isCheckedIn || alreadyCheckedIn}
              />
            )}
            <DayPermissionTag info={permission?.morningPermission} />
          </div>
        );
      },
    },
    {
      title: "Afternoon",
      key: "afternoon",
      align: "center",
      render: (record) => {
        const studentId = record?.student?._id;
        const studentState = attendanceState[studentId];
        const permission = studentState?.permission;
        const hasPermission = permission?.hasPermission;
        const alreadyCheckedOut = studentState?.is_checked_out;

        return (
          <div className="flex items-center justify-center gap-2">
            {isHoliday ? (
              <span className="text-gray-400" title="Holiday">-</span>
            ) : hasPermission ? (
              <CloseCircleFilled
                style={{ color: "#f5222d", fontSize: "20px" }}
              />
            ) : (
              <CustomCheckBox
                checked={studentState?.check_out}
                onChange={(e) =>
                  handleCheckboxChange(studentId, "check_out", e.target.checked)
                }
                disabled={isCheckedOut || alreadyCheckedOut}
              />
            )}
            <DayPermissionTag info={permission?.afternoonPermission} />
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (record) => {
        const studentId = record?.student?._id;
        const studentState = attendanceState[studentId];
        const permission = studentState?.permission;
        const hasCheckIn = !!studentState?.check_in;
        const hasCheckOut = !!studentState?.check_out;

        if (isHoliday) {
          return <Tag color="blue">Holiday</Tag>;
        }

        if (permission?.hasPermission) {
          return (
            <div className="flex flex-col items-center">
              <Tag color="green">{permission.permissionType}</Tag>
            </div>
          );
        }

        const morningExcused = !!permission?.morningPermission && !hasCheckIn;
        const afternoonExcused = !!permission?.afternoonPermission && !hasCheckOut;
        if (morningExcused || afternoonExcused) {
          const otherOk =
            (morningExcused || hasCheckIn) && (afternoonExcused || hasCheckOut);
          return (
            <Tag
              color={
                morningExcused && afternoonExcused
                  ? "blue"
                  : otherOk
                    ? "orange"
                    : "red"
              }
            >
              {morningExcused && afternoonExcused
                ? "Permission"
                : otherOk
                  ? "Half Day (Permission)"
                  : "Absent"}
            </Tag>
          );
        }

        if (hasCheckIn && hasCheckOut) {
          return <Tag color="green">Present</Tag>;
        } else if (
          (hasCheckIn && !hasCheckOut && isCheckedOut) ||
          (!hasCheckIn && hasCheckOut)
        ) {
          return <Tag color="orange">Half Day</Tag>;
        } else if (hasCheckIn || hasCheckOut) {
          return <Tag color="green">Present</Tag>;
        } else {
          return <Tag color="red">Absent</Tag>;
        }
      },
    },
  ];

  return (
    <div className="mt-8">
      <div className="mb-4 flex justify-between px-4">
        {isCheckedIn && (
          <Tag color="blue">
            Morning marked at:{" "}
            {moment(
              classDetails?.check_in_time ||
                attendanceData?.data?.result?.[0]?.updatedAt,
            ).format("LT")}
          </Tag>
        )}
        {isCheckedOut && (
          <Tag color="orange">
            Afternoon marked at:{" "}
            {moment(classDetails?.check_out_time).format("LT")}
          </Tag>
        )}
      </div>

      <Form form={form}>
        <Table
          columns={columns}
          dataSource={
            attendanceData?.data?.result
              ? Array.isArray(attendanceData.data.result)
                ? attendanceData.data.result
                : attendanceData.data.result.data || []
              : []
          }
          loading={attendanceData?.isLoading}
          pagination={false}
          rowKey={(record) => record?.student?._id}
          footer={() => {
            const isHoliday = attendanceData?.data?.result?.holiday?.isHoliday;
            const holidayName = attendanceData?.data?.result?.holiday?.name;

            return (
              <div className="flex justify-center pt-4 pb-2 gap-4">
                {!isAdmin && !isCheckedIn && !isHoliday && (
                  <Button
                    size="large"
                    type="primary"
                    className="text-white bg-green-600 hover:bg-green-500"
                    loading={isCheckingIn}
                    onClick={handleCheckIn}
                  >
                    Morning
                  </Button>
                )}
                {!isAdmin && isCheckedIn && !isCheckedOut && !isHoliday && (
                  <Button
                    size="large"
                    type="primary"
                    className="text-white bg-red-600 hover:bg-red-500"
                    loading={isCheckingOut}
                    onClick={handleCheckOut}
                  >
                    Afternoon
                  </Button>
                )}
                {isHoliday && (
                  <Tag color="red" className="text-lg p-2">
                    Holiday: {holidayName}
                  </Tag>
                )}
              </div>
            );
          }}
        />
      </Form>

      {/* Edit Modal */}
      <Modal
        title={`Edit Attendance for ${editingStudent?.studentName}`}
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleEditSave}
        confirmLoading={isSaving}
        okText="Save"
        cancelText="Cancel"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span>Morning</span>
            <CustomCheckBox
              checked={editingStudent?.check_in || false}
              onChange={(e) =>
                setEditingStudent((prev) => ({
                  ...prev,
                  check_in: e.target.checked,
                }))
              }
              disabled={false}
            />
          </div>
          <div className="flex items-center justify-between">
            <span>Afternoon</span>
            <CustomCheckBox
              checked={editingStudent?.check_out || false}
              onChange={(e) =>
                setEditingStudent((prev) => ({
                  ...prev,
                  check_out: e.target.checked,
                }))
              }
              disabled={false}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AttendanceTable;
